<template>
	<apexchart type="line" :options="options" :series="series" :height="300" />
</template>

<script>
import moment from 'moment'
import * as ACTIONS from '@/store/actions'
import { mapActions } from 'vuex'

export default {
	name: 'DetailEvolutionChart',
	props: {
		detailId: {
			required: true,
			type: Number,
		},
	},
	data: function() {
		return {
			moment,
			evo: [],
			minPost: 0,
			maxPost: 0,
			minFollower: 0,
			maxFollower: 0,
			deleted: [],
			mentions: [],
			dateFrom: moment()
				.utc()
				.subtract(1, 'months')
				.startOf('day'),
			dateTo: moment()
				.utc()
				.endOf('day'),
		}
	},
	created() {
		this.loadDetailEvo({
			where: {
				detailId: this.detailId,
				createdAt: {
					gte: this.dateFrom,
					lte: this.dateTo,
				},
			},
			limit: 50,
			order: ['createdAt ASC'],
		}).then(res => {
			this.evo = res
			this.minPost = res.length > 0 ? res[0].posts * 0.99 : 0
			this.maxPost = res.length > 0 ? res[res.length - 1].posts * 1.01 : 0

			this.minFollower = res.length > 0 ? res[0].followers * 0.99 : 0
			this.maxFollower =
				res.length > 0 ? res[res.length - 1].followers * 1.01 : 0
		})
		this.loadPost({
			filter: {
				fields: {
					createdAt: true,
					deleted: true,
				},
				where: {
					mention: {
						like: '%@salonlinebrasil%',
					},
					detailId: this.detailId,
					createdAt: {
						gte: this.dateFrom,
						lte: this.dateTo,
					},
				},
				limit: 150,
			},
		}).then(res => {
			this.mentions = res
		})
		this.loadPost({
			filter: {
				fields: {
					createdAt: true,
					deleted: true,
				},
				where: {
					deleted: true,
					detailId: this.detailId,
					createdAt: {
						gte: this.dateFrom,
						lte: this.dateTo,
					},
				},
				limit: 50,
			},
		}).then(res => {
			this.deleted = res
		})
	},
	computed: {
		annotations() {
			let deleted = this.deleted.map(d => {
				return {
					x: new Date(d.createdAt).getTime(),
					strokeDashArray: 0,
					borderColor: '#ff4560',
					label: {
						borderColor: '#ff4560',
						style: {
							color: '#fff',
							background: '#ff4560',
						},
						text: 'deleted',
					},
				}
			})
			let mentions = this.mentions.map(d => {
				return {
					x: new Date(d.createdAt).getTime(),
					strokeDashArray: 0,
					borderColor: '#FEB019',
					label: {
						borderColor: '#FEB019',
						style: {
							color: '#fff',
							background: '#FEB019',
						},
						text: 'with mention',
					},
				}
			})
			return deleted.concat(mentions)
		},
		options() {
			return {
				colors: ['#2E93fA', '#66DA26', '#FFFFFF', '#E91E63', '#FF9800'],
				chart: {
					type: 'line',
					animations: {
						enabled: false,
					},

					zoom: {
						enabled: true,
						type: 'y',
						autoScaleYaxis: true,
					},
				},
				noData: {
					text: 'No Data',
					align: 'center',
					verticalAlign: 'middle',

					color: '#FFFFFF',
					fontSize: '14px',
				},
				legend: {
					fontSize: '14px',
					labels: {
						colors: 'black',
					},
					position: 'top',
					show: true,
				},
				stroke: {
					width: 4,
					curve: 'smooth',
				},
				title: {
					show: true,
				},
				xaxis: {
					type: 'datetime',
					categories: this.evo.map(e => {
						return e.createdAt
					}),
					labels: {
						show: true,
						style: {
							colors: '#000000',
						},
					},
					axisBorder: {
						show: true,
						color: '#000000',
						height: 1,
					},
					axisTicks: {
						show: true,
					},
				},
				grid: {
					show: false,
				},
				yaxis: [
					{
						labels: {
							show: true,
							style: {
								colors: '#000000',
							},
							formatter: function(value) {
								return Math.abs(value) > 999
									? Math.sign(value) * (Math.abs(value) / 1000).toFixed(1) + 'k'
									: (Math.sign(value) * Math.abs(value)).toFixed(0)
							},
						},
						show: true,
						axisTicks: {
							show: true,
							color: '#000000',
						},
						axisBorder: {
							show: true,
							color: '#000000',
							width: 1,
						},

						title: {
							show: true,
							text: 'Posts',
						},
						stroke: {
							width: 5,
							curve: 'smooth',
						},
						max: this.maxPost,
						min: this.minPost,
					},
					{
						labels: {
							show: true,
							style: {
								colors: '#000000',
							},
							formatter: function(value) {
								return Math.abs(value) > 999
									? Math.sign(value) * (Math.abs(value) / 1000).toFixed(1) + 'k'
									: Math.sign(value) * Math.abs(value)
							},
						},
						opposite: true,
						axisTicks: {
							show: true,
							color: '#000000',
						},
						axisBorder: {
							show: true,
							color: '#000000',
							width: 1,
						},
						title: {
							text: 'Followers',
						},
						max: this.maxFollower,
						min: this.minFollower,
					},
				],
				annotations: {
					xaxis: this.annotations,
				},
			}
		},
		series() {
			const posts = {
				name: 'Posts',
				type: 'line',
				data: [],
			}
			const followers = {
				name: 'Followers',
				type: 'line',
				data: [],
			}
			for (let index = 0; index < this.evo.length; index++) {
				const evolution = this.evo[index]

				posts.data.push(evolution.posts)
				followers.data.push(evolution.followers)
			}

			if (followers.data.length === 0 && posts.data.length === 0) {
				return []
			} else {
				return [posts, followers]
			}
		},
	},
	methods: {
		...mapActions({
			loadDetailEvo: ACTIONS.EVOLUTION.EVOLUTION_POST,
			loadPost: ACTIONS.POST.FETCH_POST_BY_DETAIL_ID,
		}),
	},
}
</script>

<style></style>
